import ApplicationController from '../controllers/application_controller'
import Logger from '../lib/logger'
import { Validators, validateFileType, validateFileSize } from '../utils/validators'

export default class extends ApplicationController {
  static targets = ["field", "feedback", "file", "fileFeedback", "submit"]

  static values = {
    rules: { type: Object, default: {} },
    messages: { type: Object, default: {} },
    locale: { type: String, default: "en" },
    fileRules: { type: Object, default: {} },
    showSuccess: { type: Boolean, default: true },
    validateOnBlur: { type: Boolean, default: true },
    clearOnInput: { type: Boolean, default: true }
  }

  static defaultMessages = {
    en: {
      required: "This field is required",
      minLength: "Minimum length is {min} characters",
      maxLength: "Maximum length is {max} characters",
      pattern: "Invalid format",
      email: "Please enter a valid email address",
      phone: "Please enter a valid phone number",
      zipCode: "ZIP code must follow the format: 00000-000",
      zipCodeDigits: "ZIP code must have exactly 8 digits",
      zipCodeInvalid: "Please enter a valid ZIP code",
      cnpj: "CNPJ must follow the format: XX.XXX.XXX/XXXX-XX",
      cpf: "CPF must follow the format: XXX.XXX.XXX-XX",
      currency: "Currency must be a 3-letter ISO code (e.g., BRL, USD)",
      number: "Please enter a valid number",
      min: "Value must be at least {min}",
      max: "Value must be at most {max}",
      date: "Please enter a valid date",
      dateFuture: "Date cannot be in the past",
      latitude: "Latitude must be between -90 and 90 degrees",
      longitude: "Longitude must be between -180 and 180 degrees",
      fileType: "Please select a valid file type",
      fileSize: "File size must be less than {max}MB",
      formErrors: "Please fix the validation errors before submitting"
    },
    "pt-BR": {
      required: "Este campo é obrigatório",
      minLength: "Mínimo de {min} caracteres",
      maxLength: "Máximo de {max} caracteres",
      pattern: "Formato inválido",
      email: "Por favor, insira um email válido",
      phone: "Por favor, insira um telefone válido",
      zipCode: "CEP deve seguir o formato: 00000-000",
      zipCodeDigits: "CEP deve ter exatamente 8 dígitos",
      zipCodeInvalid: "Por favor, insira um CEP válido",
      cnpj: "CNPJ deve seguir o formato: XX.XXX.XXX/XXXX-XX",
      cpf: "CPF deve seguir o formato: XXX.XXX.XXX-XX",
      currency: "Moeda deve ser um código ISO de 3 letras (ex: BRL, USD)",
      number: "Por favor, insira um número válido",
      min: "O valor deve ser no mínimo {min}",
      max: "O valor deve ser no máximo {max}",
      date: "Por favor, insira uma data válida",
      dateFuture: "A data não pode estar no passado",
      latitude: "Latitude deve estar entre -90 e 90 graus",
      longitude: "Longitude deve estar entre -180 e 180 graus",
      fileType: "Por favor, selecione um tipo de arquivo válido",
      fileSize: "O arquivo deve ter no máximo {max}MB",
      formErrors: "Por favor, corrija os erros de validação antes de enviar"
    }
  }

  connect() {
    super.connect()
    this.setupValidation()
  }

  disconnect() {
    super.disconnect()
    this.teardownValidation()
  }

  setupValidation() {
    const form = this.getForm()
    if (!form) return

    this.boundHandlers = new Map()

    for (const field of this.fieldTargets) {
      const blurHandler = () => this.validateField(field)
      const inputHandler = () => this.clearValidation(field)

      if (this.validateOnBlurValue) field.addEventListener('blur', blurHandler)
      if (this.clearOnInputValue) field.addEventListener('input', inputHandler)

      this.boundHandlers.set(field, { blurHandler, inputHandler })
    }

    if (this.hasFileTarget) {
      const fileHandler = () => this.validateFile()
      this.fileTarget.addEventListener('change', fileHandler)
      this.boundHandlers.set(this.fileTarget, { fileHandler })
    }

    this.formSubmitHandler = (event) => this.validateFormSubmission(event)
    form.addEventListener('submit', this.formSubmitHandler)

    if (this.hasSubmitTarget) {
      this.submitClickHandler = (event) => this.validateFormSubmission(event)
      this.submitTarget.addEventListener('click', this.submitClickHandler)
    }
  }

  teardownValidation() {
    const form = this.getForm()

    this.boundHandlers?.forEach(({ blurHandler, inputHandler, fileHandler }, element) => {
      if (blurHandler) element.removeEventListener('blur', blurHandler)
      if (inputHandler) element.removeEventListener('input', inputHandler)
      if (fileHandler) element.removeEventListener('change', fileHandler)
    })
    this.boundHandlers?.clear()

    if (form && this.formSubmitHandler) {
      form.removeEventListener('submit', this.formSubmitHandler)
    }

    if (this.hasSubmitTarget && this.submitClickHandler) {
      this.submitTarget.removeEventListener('click', this.submitClickHandler)
    }
  }

  getForm() {
    return this.element.tagName === 'FORM' ? this.element : this.element.querySelector('form')
  }

  /* -- Field validation -- */

  validateField(eventOrField) {
    const field = eventOrField instanceof Event ? eventOrField.target : eventOrField
    if (!field) return true

    const value = field.value?.trim() || ''
    const fieldName = this.extractFieldName(field.name)
    const feedback = this.getFeedbackElement(field)

    this.clearValidation(field)

    const rules = this.getRulesForField(fieldName, field)
    if (!rules || Object.keys(rules).length === 0) return true

    let isValid = true
    let message = ""

    if (this.isFieldRequired(field, rules) && !value) {
      isValid = false
      message = rules.requiredMessage || this.getMessage('required')
    }

    if (isValid && value) {
      const result = this.applyValidationRules(fieldName, value, rules)
      if (!result.isValid) {
        isValid = false
        message = result.message
      }
    }

    if (!isValid) {
      this.showValidationError(field, feedback, message)
    } else if (this.showSuccessValue && value) {
      this.showValidationSuccess(field, feedback)
    }

    return isValid
  }

  getRulesForField(fieldName, field) {
    const dataAttr = field.dataset.validationRules
    if (dataAttr) {
      try { return JSON.parse(dataAttr) } catch { /* ignore */ }
    }

    const fieldPath = this.extractFieldPath(field.name)
    return this.rulesValue[fieldPath] || this.rulesValue[fieldName] || {}
  }

  isFieldRequired(field, rules) {
    return field.hasAttribute('required') || rules.required === true
  }

  applyValidationRules(fieldName, value, rules) {
    if (rules.validator) {
      const result = this.runValidator(rules.validator, value, rules)
      if (!result.isValid) return result
    }

    if (rules.pattern) {
      const pattern = typeof rules.pattern === 'string' ? new RegExp(rules.pattern) : rules.pattern
      if (!pattern.test(value)) {
        return { isValid: false, message: rules.patternMessage || this.getMessage('pattern') }
      }
    }

    if (rules.minLength !== undefined && value.length < rules.minLength) {
      return { isValid: false, message: rules.minLengthMessage || this.getMessage('minLength', { min: rules.minLength }) }
    }

    if (rules.maxLength !== undefined && value.length > rules.maxLength) {
      return { isValid: false, message: rules.maxLengthMessage || this.getMessage('maxLength', { max: rules.maxLength }) }
    }

    if (rules.min !== undefined) {
      const num = parseFloat(value)
      if (isNaN(num) || num < rules.min) {
        return { isValid: false, message: rules.minMessage || this.getMessage('min', { min: rules.min }) }
      }
    }

    if (rules.max !== undefined) {
      const num = parseFloat(value)
      if (isNaN(num) || num > rules.max) {
        return { isValid: false, message: rules.maxMessage || this.getMessage('max', { max: rules.max }) }
      }
    }

    return { isValid: true }
  }

  runValidator(validatorName, value) {
    const validator = Validators[validatorName]
    if (validator) return validator(value)

    Logger.warn(`Unknown validator "${validatorName}"`)
    return { isValid: true }
  }

  /* -- File validation -- */

  validateFile() {
    if (!this.hasFileTarget) return true

    const file = this.fileTarget.files[0]
    if (!file) return true

    const feedback = this.hasFileFeedbackTarget ? this.fileFeedbackTarget : null
    const rules = this.fileRulesValue

    if (rules.allowedTypes?.length > 0) {
      if (!validateFileType(file, rules.allowedTypes)) {
        const message = rules.typeMessage || this.getMessage('fileType')
        this.showFileValidationError(this.fileTarget, feedback, message)
        return false
      }
    }

    if (rules.maxSize) {
      if (!validateFileSize(file, rules.maxSize)) {
        const message = rules.sizeMessage || this.getMessage('fileSize', { max: Math.round(rules.maxSize / (1024 * 1024)) })
        this.showFileValidationError(this.fileTarget, feedback, message)
        return false
      }
    }

    this.clearFileValidation(this.fileTarget, feedback)
    return true
  }

  /* -- Form validation orchestration (shared by submit, click, form submission) -- */

  validateAllFields() {
    const form = this.getForm()
    if (!form) return true

    let isValid = true
    form.querySelectorAll('[data-form-validation-target="field"]').forEach(field => {
      if (!this.validateField(field)) isValid = false
    })

    if (this.hasFileTarget && !this.validateFile()) isValid = false

    return isValid
  }

  validateFormSubmission(event) {
    if (!this.validateAllFields()) {
      event?.preventDefault()
      this.showFormErrors()
      return false
    }

    return true
  }

  submitForm(event) {
    if (!this.validateAllFields()) {
      event?.preventDefault()
      this.showFormErrors()
      return false
    }

    const form = this.getForm()
    if (form) form.requestSubmit()
    return true
  }

  /* -- I18n messages -- */

  getMessage(key, params = {}) {
    let message = this.messagesValue[key]

    if (!message) {
      const locale = this.localeValue || 'en'
      const defaults = this.constructor.defaultMessages[locale] || this.constructor.defaultMessages.en
      message = defaults[key] || key
    }

    if (params && typeof message === 'string') {
      for (const [paramKey, paramVal] of Object.entries(params)) {
        message = message.replace(new RegExp(`\\{${paramKey}\\}`, 'g'), paramVal)
      }
    }

    return message
  }

  /* -- UI feedback -- */

  showValidationError(field, feedback, message) {
    field.classList.add('is-invalid')
    field.classList.remove('is-valid')
    if (feedback) {
      feedback.textContent = message
      feedback.classList.add('invalid-feedback', 'd-block')
      feedback.classList.remove('valid-feedback')
    }
  }

  showValidationSuccess(field, feedback) {
    field.classList.add('is-valid')
    field.classList.remove('is-invalid')
    if (feedback) {
      feedback.classList.add('valid-feedback', 'd-block')
      feedback.classList.remove('invalid-feedback')
    }
  }

  showFileValidationError(fileInput, feedback, message) {
    fileInput.classList.add('is-invalid')
    if (feedback) {
      feedback.textContent = message
      feedback.classList.add('invalid-feedback', 'd-block')
    }
  }

  clearValidation(field) {
    field.classList.remove('is-invalid', 'is-valid')
    const feedback = this.getFeedbackElement(field)
    if (feedback) {
      feedback.classList.remove('d-block')
      feedback.textContent = ''
    }
  }

  clearFileValidation(fileInput, feedback) {
    fileInput.classList.remove('is-invalid')
    if (feedback) {
      feedback.classList.remove('d-block')
      feedback.textContent = ''
    }
  }

  getFeedbackElement(field) {
    const fieldId = field.id
    const fieldName = this.extractFieldName(field.name)

    if (fieldId) {
      const byId = this.element.querySelector(`[data-form-validation-target="feedback"][data-for="${fieldId}"]`)
      if (byId) return byId
    }

    if (fieldName) {
      const byName = this.element.querySelector(`[data-form-validation-target="feedback"][data-for="${fieldName}"]`)
      if (byName) return byName
    }

    return field.parentElement?.querySelector('[data-form-validation-target="feedback"]') || null
  }

  showFormErrors() {
    const firstError = this.element.querySelector('.is-invalid')
    if (firstError) {
      firstError.scrollIntoView({ behavior: 'smooth', block: 'center' })
      firstError.focus()
    }

    window.dispatchEvent(new CustomEvent('toast:show', {
      detail: { message: this.getMessage('formErrors'), type: 'error' }
    }))
  }

  /* -- Public API -- */

  isFormValid() {
    return this.validateAllFields()
  }

  resetValidation() {
    this.fieldTargets.forEach(field => this.clearValidation(field))
    if (this.hasFileTarget && this.hasFileFeedbackTarget) {
      this.clearFileValidation(this.fileTarget, this.fileFeedbackTarget)
    }
  }

  addRules(newRules) {
    this.rulesValue = { ...this.rulesValue, ...newRules }
  }

  removeRules(fieldName) {
    const { [fieldName]: _removed, ...rest } = this.rulesValue
    this.rulesValue = rest
  }

  /* -- Helpers -- */

  extractFieldName(fieldName) {
    if (!fieldName) return ''
    const match = fieldName.match(/\[([^\]]+)\]$/)
    return match ? match[1] : fieldName
  }

  extractFieldPath(fieldName) {
    if (!fieldName) return ''
    const matches = fieldName.match(/\[([^\]]+)\]/g)
    if (!matches) return fieldName
    return matches.map(m => m.slice(1, -1)).join('.')
  }
}
